<template>
  <section v-if="!loadStatus">
    <Loading text="Loading..." />
  </section>
  <section v-else>
    <div class="arrow-link">
      <router-link :to="{ name: 'profile' }">
        <span class="inter">Back</span>
      </router-link>
    </div>
    <div class="right-column">
      <p class="description editorial">
        Single Sign-On using IdP
      </p>
      <p class="header font-semibold">
        Single Sign-On (SSO) using IdP enables you to authenticate and access
        applications via Gemnote and automate the exchange of identity
        information, so that you can provision users and groups to sign into
        multiple applications with a single credential.
      </p>
      <p class="header">
        For users to access your applications via Gemnote, they must first
        authenticate using any SAML 2.0-compliant identity provider (IdP) with
        the service provider-initiated (SP-initiated) model. The IdP must be
        configured to recognize Gemnote as a valid SP, and you must configure
        the full details for the IdP within the Gemnote Advanced Settings.
      </p>

      <!-- Tip Section -->
      <section class="tip-section">
        <div class="flex items-center gap-1 tip-section__header">
          <img src="../../assets/tip.svg" />
          <h4>Tip</h4>
        </div>

        <ul>
          <li>
            <span>
              Retrieve the following values from your IdP for Gemnote Single
              Sign-On using IdP, <br />
            </span>

            <span class="text-muted">
              Issuer / IdP Entity ID (required), IdP SSO Target URL, IdP Cert
              Fingerprint & IdP Cert Fingerprint Algorithm</span
            >
          </li>

          <li><span>Fill all required fields (*) in Configuration.</span></li>
        </ul>
      </section>

      <!-- Assertion Customer Service (ASL) URL* -->
      <p class="description editorial">
        Configuration
      </p>

      <div class="sso-settings-container">
        <section class="sso-settings-dafault">
          <div class="sso-default-container">
            <label class="label-container flex items-center gap-3">
              <p>Single Sign-On</p>

              <div class="toggle-info">
                <label
                  class="toggle-button"
                  :class="!existingIDPProfile && 'disabled'"
                >
                  <input
                    type="checkbox"
                    :disabled="!existingIDPProfile || updatingIDPSettings"
                    :checked="IDPProfile.enabled"
                    :v-model="IDPProfile.enabled"
                    @change="handleSSOToggle($event)"
                  />
                  <span class="toggle-slider round cursor-not-allowed"></span>
                </label>
              </div>
            </label>
            <div></div>
          </div>
          <div class="sso-default-container">
            <label class="label-container flex items-center gap-3">
              <p>Assertion Customer Service (ASL) URL*</p>
            </label>
            <div class="w-full overflow-hidden">
              <CopyBanner
                :text="IDPProfile.serviceUrl"
                @click="doCopy(IDPProfile.serviceUrl)"
                :isIconMuted="true"
                customTextStyles="font-weight: 300; font-size: 0.8rem"
              />
            </div>
          </div>
          <div class="sso-default-container">
            <label class="label-container flex items-center gap-3">
              <p>Assertion Customer Service Binding</p>
            </label>
            <div class="w-full overflow-hidden">
              <CopyBanner
                :text="IDPProfile.assertionUrl"
                @click="doCopy(IDPProfile.assertionUrl)"
                :isIconMuted="true"
                customTextStyles="font-weight: 300; font-size: 0.8rem"
              />
            </div>
          </div>
          <div class="sso-default-container mb-0">
            <label class="label-container flex items-center gap-3">
              <p>Name Identifier Format</p>
            </label>
            <div class="w-full overflow-hidden">
              <CopyBanner
                :text="IDPProfile.nameIdentifierFormat"
                @click="doCopy(IDPProfile.nameIdentifierFormat)"
                :isIconMuted="true"
                customTextStyles="font-weight: 300; font-size: 0.8rem"
              />
            </div>
          </div>
        </section>

        <form @submit.prevent>
          <section>
            <!-- SP SLO Endpoint URL -->
            <div class="settings-option mt-0 relative">
              <label class="label-container gap-1">
                <p>Issuer /IdP Entity ID*</p>

                <span
                  v-if="
                    IDPProfile.idpEntityId && !$v.IDPProfile.idpEntityId.$error
                  "
                >
                  <img src="../../assets//success-check.svg" alt="" />
                </span>
              </label>
              <input
                type="text"
                v-model="IDPProfile.idpEntityId"
                placeholder="e.g. https://idp.example.com/saml/slo"
                class="form-control"
                :class="{ input__error: $v.IDPProfile.idpEntityId.$error }"
                @blur="$v.IDPProfile.idpEntityId.$touch()"
                @input="$v.IDPProfile.idpEntityId.$touch(), (isEditing = true)"
              />
              <span
                class="error-message-info  bottom"
                v-if="$v.IDPProfile.idpEntityId.$error"
                >{{
                  !$v.IDPProfile.idpEntityId.required
                    ? 'Required field.'
                    : 'Invalid input. Please try again.'
                }}
              </span>
            </div>

            <!--  -->
            <div class="settings-option relative">
              <label class="label-container gap-1">
                <p>IdP SSO Target URL*</p>

                <span
                  v-if="
                    IDPProfile.idpSsoTargetUrl &&
                      !$v.IDPProfile.idpSsoTargetUrl.$error
                  "
                >
                  <img src="../../assets//success-check.svg" alt="" />
                </span>
              </label>

              <input
                type="text"
                v-model="IDPProfile.idpSsoTargetUrl"
                placeholder="e.g. https://idp.example.com/saml/sso"
                class="form-control"
                :class="{ input__error: $v.IDPProfile.idpSsoTargetUrl.$error }"
                @blur="$v.IDPProfile.idpSsoTargetUrl.$touch()"
                @input="
                  $v.IDPProfile.idpSsoTargetUrl.$touch(), (isEditing = true)
                "
              />
              <span
                class="error-message-info  bottom"
                v-if="$v.IDPProfile.idpSsoTargetUrl.$error"
                >{{
                  !$v.IDPProfile.idpSsoTargetUrl.required
                    ? 'Field is required.'
                    : 'Invalid input. Please try again.'
                }}
              </span>
            </div>
            <!--  -->
            <div class="settings-option relative">
              <label class="label-container gap-1">
                <p>IdP Cert Fingerprint*</p>

                <span
                  v-if="
                    IDPProfile.idpCertFingerprint &&
                      !$v.IDPProfile.idpCertFingerprint.$error
                  "
                >
                  <img src="../../assets//success-check.svg" alt="" />
                </span>
              </label>

              <input
                type="text"
                v-model="IDPProfile.idpCertFingerprint"
                placeholder="e.g. AB:CD:EF:12:34:56:78:90:AB:CD:EF:12:34:56:78:90:AB:CD:EF:12"
                class="form-control"
                :class="{
                  input__error: $v.IDPProfile.idpCertFingerprint.$error
                }"
                @blur="$v.IDPProfile.idpCertFingerprint.$touch()"
                @input="
                  $v.IDPProfile.idpCertFingerprint.$touch(), (isEditing = true)
                "
              />
              <span
                class="error-message-info  bottom"
                v-if="$v.IDPProfile.idpCertFingerprint.$error"
                >{{
                  !$v.IDPProfile.idpCertFingerprint.required
                    ? 'Field is required.'
                    : 'Invalid input. Please try again.'
                }}
              </span>
            </div>

            <!--  -->
            <div class="settings-option relative">
              <label class="label-container gap-1">
                <p>IdP Cert Fingerprint Algorithm*</p>

                <span
                  v-if="
                    IDPProfile.idpCertFingerprintAlgorithm &&
                      !$v.IDPProfile.idpCertFingerprintAlgorithm.$error
                  "
                >
                  <img src="../../assets//success-check.svg" alt="" />
                </span>
              </label>

              <input
                type="text"
                v-model="IDPProfile.idpCertFingerprintAlgorithm"
                placeholder="e.g. SHA-1"
                class="form-control"
                :class="{
                  input__error: $v.IDPProfile.idpCertFingerprintAlgorithm.$error
                }"
                @blur="$v.IDPProfile.idpCertFingerprintAlgorithm.$touch()"
                @input="
                  $v.IDPProfile.idpCertFingerprintAlgorithm.$touch(),
                    (isEditing = true)
                "
              />
              <span
                class="error-message-info  bottom"
                v-if="$v.IDPProfile.idpCertFingerprintAlgorithm.$error"
                >{{
                  !$v.IDPProfile.idpCertFingerprintAlgorithm.required
                    ? 'Field is required.'
                    : 'Invalid input. Please try again.'
                }}
              </span>
            </div>
            <!--  -->

            <div class="settings-option relative">
              <label class="label-container gap-1">
                <p>AuthnContext (optional)</p>
                <span
                  v-if="
                    IDPProfile.authnContext &&
                      !$v.IDPProfile.authnContext.$error
                  "
                >
                  <img src="../../assets//success-check.svg" alt="" />
                </span>
              </label>

              <input
                type="text"
                @input="isEditing = true"
                v-model="IDPProfile.authnContext"
                placeholder="e.g. urn:oasis:names:tc:SAML:2.0:ac:classes:PasswordProtectedTransport"
                class="form-control"
              />
            </div>

            <!--  -->
            <div class="settings-option relative">
              <label class="label-container gap-1">
                <p>IdP SLO Target URL (optional)</p>
                <span
                  v-if="
                    IDPProfile.idpSloTargetUrl &&
                      !$v.IDPProfile.idpSloTargetUrl.$error
                  "
                >
                  <img src="../../assets//success-check.svg" alt="" />
                </span>
              </label>

              <input
                type="text"
                v-model="IDPProfile.idpSloTargetUrl"
                placeholder="e.g. https://idp.example.com/saml/slo"
                class="form-control"
                :class="{
                  input__error: $v.IDPProfile.idpSloTargetUrl.$error
                }"
                @blur="$v.IDPProfile.idpSloTargetUrl.$touch()"
                @input="
                  $v.IDPProfile.idpSloTargetUrl.$touch(), (isEditing = true)
                "
              />
              <span
                class="error-message-info  bottom"
                v-if="$v.IDPProfile.idpSloTargetUrl.$error"
              >
                Invalid input. Please try again.
              </span>
            </div>
          </section>
          <div class="settings_cta">
            <button
              type="submit"
              class="button-gemnote inventory-btn button-gemnote--small"
              :disabled="isSaveBtnDisabled"
              @click="handleSaveSettings"
            >
              <span>Save </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { required, url } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import { progressStatus } from '../../helpers'
import CopyBanner from '@/components/Universal/CopyBanner'

export default {
  name: 'SSOSettings',

  components: { CopyBanner },

  data() {
    return {
      orderConfirmation: false,
      isEditing: false,
      isBtnDisabled: false,
      isSSOEnabled: false,
      content: {
        serviceUrl: 'Assertion Customer Service (ASL) URL.',
        csBinding: 'Assertion Customer Service Binding.',
        nameIdentifierFormat: 'Name Identifier Format.',
        idpEntityId: 'Enter Issuer / Identity Provider (IDP) entity id.',
        authnContext: 'Enter authentication context.',
        idpSloTargetUrl:
          'Enter Identity Provider (IDP) Single Logout (SLO) target url.',
        assertionUrl: 'Assertion Customer Service Binding.',
        idpSsoTargetUrl:
          'Enter Identity Provider (IDP) Single Sign On (SSO) target url.',
        idpCertFingerprint:
          'Enter Identity Provider (IDP) certificate fingerprint.',
        idpCertFingerprintAlgorithm:
          'Enter Identity Provider (IDP) certificate fingerprint algorithm.'
      }
    }
  },

  validations: {
    IDPProfile: {
      idpEntityId: {
        required
      },
      idpSsoTargetUrl: {
        required,
        url
      },
      idpCertFingerprint: {
        required
      },
      idpCertFingerprintAlgorithm: {
        required
      },
      idpSloTargetUrl: {
        url
      },
      authnContext: {}
    }
  },
  methods: {
    doCopy(textToCopy) {
      if (textToCopy) {
        this.$copyText(textToCopy)
        this.$store.dispatch('notification/pushNotification', {
          type: 'success',
          messages: [`Link copied`]
        })
      }
    },

    updateUserProfile(value) {
      this.$store.dispatch('profile/updateUserProfile', {
        hideNotification: value.value ? value.value : false
      })
    },

    async handleSaveSettings() {
      this.$v.IDPProfile.$touch()
      if (this.$v.IDPProfile.$error === true) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'warning',
          messages: [`Please make sure all required fields (*) are filled in.`]
        })
        return
      }
      this.isBtnDisabled = true
      if (this.existingIDPProfile) {
        await this.$store.dispatch('profile/updateIDPSetup', this.IDPProfile)
      } else {
        await this.$store.dispatch('profile/createIDPSetup', this.IDPProfile)
      }
      this.isBtnDisabled = false
      this.isEditing = false
    },

    async handleSSOToggle(e) {
      const isChecked = e.target.checked
      if (isChecked) {
        await this.$store.dispatch('profile/enableIDPSettings')
      } else {
        await this.$store.dispatch('profile/disbleIDPSettings')
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      loadUserProfile: state => state.profile.loadUserProfile,
      updatigUserProfile: state => state.profile.updatigUserProfile,
      IDPProfile: state => state.profile.IDPProfile,
      existingIDPProfile: state => state.profile.existingIDPProfile,
      updatingIDPSettings: state => state.profile.updatingIDPSettings
    }),
    tooltipPosition() {
      if (this.innerWidth < 1000) {
        return 'bottom-start'
      }
      return 'right'
    },
    loadStatus() {
      return progressStatus(this.loadUserProfile)
    },
    isSaveBtnDisabled() {
      return this.isBtnDisabled || !this.isEditing
    }
  },
  created() {
    this.$store.dispatch('profile/getIDPSettings')
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

.right-column {
  margin-top: 3rem;
  padding: 2.5rem 2.5rem;
  background: inherit;
  border: 1px solid $onyx;
  border-radius: 29px;
  margin-bottom: 2rem;

  p.header {
    text-align: left;
    padding-bottom: 1rem;
    font-size: 0.813rem;
  }

  .description {
    font-weight: 400;
    text-align: left;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
}

.sso-settings-container {
  border: 1px solid #dfdad2;
  border-radius: 12px;

  .sso-settings-dafault {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #dfdad2;
  }

  .sso-default-container {
    display: grid;
    grid-template-columns: 45% 1fr;
    margin-bottom: 1rem;

    &.mb-0 {
      margin-bottom: 0;
    }
  }
  .settings-option {
    margin: 3rem 0rem;
    max-width: 550px;

    &.mt-0 {
      margin-top: 0px;
    }

    input {
      margin-top: 1.3rem;
      height: 100%;
      padding-bottom: 4px;

      &.input__error {
        border-bottom: 1px solid $gem-terraorange !important;
      }

      &::placeholder {
        font-size: 0.813rem;
      }
    }
    &.mt-0 {
      margin-top: 0px;
    }
  }

  form {
    padding: 1.5rem 2rem;
  }
}

.tip-section {
  background-color: #dfdad2;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;

  .tip-section__header {
    img {
      width: 20px;
    }

    h4 {
      font-size: 0.975rem;
    }
  }
  ul {
    list-style-type: decimal;
    margin-left: 2rem;
    margin-top: 0.5rem;
    li {
      font-size: 0.813rem;
      text-align: left;
      line-height: 1.5rem;
      margin-top: 0.2rem;

      span {
        font-weight: 300;
        margin-left: 0.2rem;
        &.text-muted {
          color: #514d43;
          font-weight: 600;
        }
      }
    }
  }
}

.settings_cta {
  margin-top: 3rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  button {
    min-width: 100px;
    max-width: 100px;
    height: 2.5rem;

    &:disabled {
      background-color: #a09c95;
      color: #f5f2ee;
      border-color: #a09c95;
      cursor: not-allowed;
    }
  }
}

.label-container {
  display: flex;
  align-items: center;
  p {
    text-align: left;
    font-size: 0.813rem;
    align-items: center;
    font-weight: 700;
  }

  .tool-tip {
    margin: -3px 0px 0px 5px;
  }
}

.toggle-button.disabled {
  opacity: 0.4;
  cursor: not-allowed;

  * {
    cursor: not-allowed;
  }
}

.error-message-info {
  position: absolute;
  bottom: 0;
  left: 0%;
  font-size: 10px;
  color: #ce3c3c;
  font-weight: 600;
  text-align: left;

  &.bottom {
    top: 101%;
    right: 0;
  }
}

@media (max-width: 768px) {
  .right-column {
    padding: 1.5rem;
  }
  .sso-settings-container {
    .sso-settings-dafault {
      padding: 1rem;
    }

    .sso-default-container {
      grid-template-columns: 1fr;
      margin-bottom: 1rem;
      gap: 0.5rem;
    }

    form {
      padding: 1rem;
    }
  }

  .tip-section {
    padding: 1rem;
  }
}
</style>
